import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { PayCodesResponse } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

type Classification = { code: string; isExcused: boolean; isNoPunchExpected: boolean };
type PayCodeLine = Classification & { unclassifiedRows?: number };

/**
 * Pay codes decide whether a day is scored at all, so these two checkboxes are
 * the highest-leverage reference data in the system. They arrive with the
 * punch data Admin loads, and every app that reads punches uses the same list,
 * which is why they live here rather than in one app's settings.
 *
 * Unclassified codes are listed first and deliberately loudly: the generator
 * holds those days at zero points rather than guessing, so an unclassified
 * code is unfinished work, not a steady state.
 */
export function PayCodes() {
  const { can } = useApp();
  const queryClient = useQueryClient();
  const readOnly = !can('reference', 'edit');
  const [error, setError] = useState<string | null>(null);

  const payCodes = useQuery({
    queryKey: ['pay-codes'],
    queryFn: () => api.get<PayCodesResponse>('/api/pay-codes'),
  });

  // The code being saved. Each checkbox sends both flags from what it rendered
  // with, so a second tick before the refetch lands would undo the first; the
  // row stays disabled until the saved values are back on screen.
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (v: Classification) => { setPendingCode(v.code); return api.put('/api/pay-codes', v); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pay-codes'] }),
    onError: (e: Error) => setError(e.message),
    onSettled: () => setPendingCode(null),
  });

  const unclassified = payCodes.data?.unclassified ?? [];
  const { rows, sort, onSort } = useTableSort<PayCodeLine>([
    ...unclassified.map((u) => ({ code: u.code, isExcused: false, isNoPunchExpected: false, unclassifiedRows: u.punchRows })),
    ...(payCodes.data?.classified ?? []).map((c) => ({ code: c.code, isExcused: c.isExcused, isNoPunchExpected: c.isNoPunchExpected })),
  ], {
    code: (p) => p.code,
    excused: (p) => p.isExcused,
    noPunch: (p) => p.isNoPunchExpected,
    effect: effectOf,
  });
  // Unclassified codes stay on top however the table is sorted: they are unfinished work.
  const listed = [
    ...rows.filter((p) => p.unclassifiedRows !== undefined),
    ...rows.filter((p) => p.unclassifiedRows === undefined),
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Pay Codes</div>
          <div className="page-subtitle">What each time-clock code means, for every app that reads punch data</div>
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div className="meta" style={{ marginBottom: 10 }}>
        <strong>Excused</strong> means the day is not evaluated at all — no lateness check, no absence, auto-approved.{' '}
        <strong>No punch expected</strong> means a missing punch is normal, so no absence is charged, but the day still
        waits for review. Neither box ticked means the day is measured against the schedule, where an absence costs points.
      </div>

      {unclassified.length > 0 && (
        <div className="banner banner--conflict" style={{ marginBottom: 12 }}>
          <strong>{unclassified.length} pay {unclassified.length === 1 ? 'code' : 'codes'} in your punch data {unclassified.length === 1 ? 'is' : 'are'} not classified.</strong>{' '}
          Those days are held at 0 points and are waiting for review. Tick the boxes below, then use
          <strong> Rebuild daily records</strong> on the Facility Scorecard's Daily Summary screen.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="CODE" column="code" sort={sort} onSort={onSort} />
              <SortableHeader label="EXCUSED" column="excused" sort={sort} onSort={onSort} className="num" />
              <SortableHeader label="NO PUNCH EXPECTED" column="noPunch" sort={sort} onSort={onSort} className="num" />
              <SortableHeader label="EFFECT" column="effect" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {payCodes.isLoading && <EmptyRow colSpan={4}>Loading…</EmptyRow>}

            {listed.map((p) => (
              <PayCodeRowView
                key={`${p.unclassifiedRows !== undefined ? 'u-' : ''}${p.code}`}
                code={p.code}
                isExcused={p.isExcused}
                isNoPunchExpected={p.isNoPunchExpected}
                unclassifiedRows={p.unclassifiedRows}
                readOnly={readOnly}
                busy={pendingCode !== null}
                onSave={(v) => { setError(null); save.mutate(v); }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayCodeRowView({
  code, isExcused, isNoPunchExpected, unclassifiedRows, readOnly, busy, onSave,
}: {
  code: string;
  isExcused: boolean;
  isNoPunchExpected: boolean;
  unclassifiedRows?: number;
  readOnly: boolean;
  /** A save is in flight; every row waits so none acts on values about to change. */
  busy: boolean;
  onSave: (v: Classification) => void;
}) {
  const effect = effectOf({ code, isExcused, isNoPunchExpected, unclassifiedRows });

  return (
    <tr style={unclassifiedRows !== undefined ? { background: 'var(--orange-tint)' } : undefined}>
      <td className="strong">{code}</td>
      <td className="num">
        <input
          type="checkbox"
          checked={isExcused}
          disabled={readOnly || busy}
          onChange={(e) => onSave({ code, isExcused: e.target.checked, isNoPunchExpected })}
        />
      </td>
      <td className="num">
        <input
          type="checkbox"
          checked={isNoPunchExpected}
          disabled={readOnly || busy}
          onChange={(e) => onSave({ code, isExcused, isNoPunchExpected: e.target.checked })}
        />
      </td>
      <td className="meta">{effect}</td>
    </tr>
  );
}

/** What a classification does to a day, in words. */
function effectOf({ isExcused, isNoPunchExpected, unclassifiedRows }: PayCodeLine): string {
  return unclassifiedRows !== undefined
    ? `Unclassified — ${unclassifiedRows} row${unclassifiedRows === 1 ? '' : 's'} held at 0 points, pending`
    : isExcused
      ? 'Not evaluated — 0 points, auto-approved'
      : isNoPunchExpected
        ? 'Missing punch is normal — 0 points, pending'
        : 'Measured against the schedule';
}
